import { useCallback, useState } from 'react';

import { useMountEffect } from '@react-hookz/web';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import partialRight from 'lodash/partialRight';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import SubmitButton from 'src/app/components/Elements/Button/Submit';
import { Card } from 'src/app/components/Elements/Card';
import LinkToCardItem from 'src/app/components/Elements/Card/LinkToCardItem';
import RadioCardItem from 'src/app/components/Elements/Card/RadioCardItem';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import FixedFloat from 'src/app/components/Elements/Page/Standard/FixedFloat';
import Spinner from 'src/app/components/Elements/Spinner';
import { TypographyRows } from 'src/app/components/Elements/Typography/Sections';
import { CardItemOverflowMenu } from 'src/app/components/PageSections/CardItemOverflowMenuWrapper';
import PaymentCard from 'src/app/components/PaymentCard/container';
import { checkoutAddCreditCard } from 'src/app/router/paths';
import { closeModal, openModal, segmentTrack } from 'src/app/state/actions';
import { accountLoadEverything, deletePaymentCard } from 'src/app/state/actions/account';
import { bffFetchCart, updateCartPaymentMethod } from 'src/app/state/actions/cart';
import { pushNextCheckoutState } from 'src/app/state/actions/checkout';
import {
  getAccountDefaultCardId,
  getAccountIsFetching,
  getBFFCart,
  getBFFCartPaymentMethod,
  getCurrentAccountId,
  getCurrentCartId,
  getCurrentCartUUID,
  getIsBffCartFetching,
  getPaymentCards,
} from 'src/app/state/selectors';
import { useSelector, useDispatch } from 'src/app/state/store';
import type { UpdateCartPaymentMethodRequest } from 'src/app/types';
import { CAPSULE_ICON_STRINGS, POPUP_MENU_LABELS } from 'src/consts';

import { AddANewAddressContainer, AddIcon } from '../../DeliveryAddress/Select/styles';

import styles from './styles.module.scss';

export const PaymentCardSelect = () => {
  const cartId = useSelector(getCurrentCartId);
  const cartUUID = useSelector(getCurrentCartUUID);
  const accountId = useSelector(getCurrentAccountId);
  const isAccountFetching = useSelector(getAccountIsFetching);
  const bffCart = useSelector(getBFFCart);
  const isBffCartFetching = useSelector(getIsBffCartFetching);
  const paymentCards = useSelector(getPaymentCards);

  const defaultCardId = useSelector(getAccountDefaultCardId);
  const activeCardId = useSelector(getBFFCartPaymentMethod)?.id || defaultCardId;

  const dispatch = useDispatch();

  const dispatchLoadCart = (cartId: string) => dispatch(bffFetchCart(cartId));
  const dispatchLoadAccount = () => dispatch(accountLoadEverything());
  const dispatchUpdateCartPaymentMethod = async (body: UpdateCartPaymentMethodRequest) => {
    if (body.paymentMethodId) {
      await dispatch(updateCartPaymentMethod(body));
    }
    await dispatch(pushNextCheckoutState());
  };
  const dispatchOpenModal = ({ modalType, handleConfirmClick, confirmMessage }) =>
    dispatch(openModal({ modalType, handleConfirmClick, confirmMessage }));
  const dispatchCloseModal = () => dispatch(closeModal());

  const dispatchDeletePaymentCard = (accountId: any, cardId: any) => dispatch(deletePaymentCard(accountId, cardId));
  const dispatchSegmentTrack = useCallback(
    (event: UnifiedEvent, payload: any) => dispatch(segmentTrack(event, payload) as any),
    [dispatch]
  );
  const [selectedCard, setSelectedCard] = useState(activeCardId);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useMountEffect(() => {
    dispatchSegmentTrack(UNIFIED.viewManagePayment, { source: 'checkout' });
    dispatchLoadAccount();

    if (!bffCart && !isBffCartFetching) {
      dispatchLoadCart(cartId);
    }
  });

  const selectCard = (e, id) => {
    e.preventDefault();
    dispatchUpdateCartPaymentMethod({
      paymentMethodId: id,
      cartUUID,
    });
    setIsSubmitting(true);
  };

  const handleDeleteCard = async ({ accountId, paymentCardId }: { accountId: string; paymentCardId: string }) => {
    setIsSubmitting(true);
    dispatchCloseModal();
    await dispatchDeletePaymentCard(accountId, paymentCardId);
    dispatchSegmentTrack(UNIFIED.deleteCreditCard, { source: 'Checkout' });
    setIsSubmitting(false);
  };
  const isActive = (id: string) => (selectedCard ? selectedCard === id : activeCardId === id);

  const handleDelete = ({ accountId, paymentCardId }: { accountId: string; paymentCardId: string }) => {
    dispatchOpenModal({
      modalType: 'confirm',
      handleConfirmClick: () => handleDeleteCard({ accountId, paymentCardId }),
      confirmMessage: 'Are you sure you want to delete this card?',
    });
  };

  const getMenuActions = ({ accountId, paymentCardId }: { accountId: string; paymentCardId: string }) => {
    const menuActions = {
      items: [{ label: POPUP_MENU_LABELS.delete, action: async () => handleDelete({ accountId, paymentCardId }) }],
    };
    return menuActions;
  };

  if (isBffCartFetching || isAccountFetching || isSubmitting) {
    <StandardPage title={'Choose a credit card'} alignment={'left'}>
      <Spinner centered />
    </StandardPage>;
  }

  return (
    <StandardPage title={'Choose a credit card'} alignment={'left'}>
      <div>
        <Card>
          {map(paymentCards, ({ id: paymentCardId }) => (
            <RadioCardItem
              key={paymentCardId}
              onClick={() => setSelectedCard(paymentCardId)}
              isActive={isActive(paymentCardId)}
            >
              {!isActive(paymentCardId) && (
                <div className={styles.menu}>
                  <CardItemOverflowMenu menuActions={getMenuActions({ accountId, paymentCardId })} />
                </div>
              )}
              <PaymentCard paymentCardId={paymentCardId} />
            </RadioCardItem>
          ))}
          <LinkToCardItem to={checkoutAddCreditCard.toPath({ cartId })} data-cy={'add-card'} showArrow={false}>
            <AddANewAddressContainer>
              <AddIcon iconString={CAPSULE_ICON_STRINGS.add_icon} block />
              <TypographyRows textList={['Add a new payment']} />
            </AddANewAddressContainer>
          </LinkToCardItem>
        </Card>
        <FixedFloat>
          <SubmitButton
            style="continueButton"
            isSubmitting={false}
            data-cy={'checkout-place-order'}
            onClick={partialRight(selectCard, selectedCard)}
            disabled={isSubmitting || isEmpty(paymentCards)}
          >
            Continue
          </SubmitButton>
        </FixedFloat>
      </div>
    </StandardPage>
  );
};
